import React from "react";

import loggy from "@catchoftheday/loggy";

import { Experiment as ExperimentType } from "@analytics/generated/graphql";

import { useExperimentContext } from "./context";

type UseExperimentPayload = {
  /** Experiment Status */
  variants: (ExperimentType | null)[];
  /** Whether the experiments are being fetched */
  loading: boolean;
  /** Whether there was an error fetching experiments */
  error: boolean;
  /** Retry fetching of experiments */
  retry: () => void;
};

/**
 * @arg experiments - List of experiments to retrieve
 * @returns List of experiment buckets in the same order
 */
export const useExperiment = (experiments: string[]): UseExperimentPayload => {
  const experimentContext = useExperimentContext();
  const [variants, setVariants] = React.useState<(ExperimentType | null)[]>(
    experiments.map(() => null)
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  const fetchExperiments = React.useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      const results = await experimentContext.query(experiments);

      setVariants(results);
    } catch (e) {
      loggy.error(e);
      setError(true);
    }
    setLoading(false);
  }, [...experiments]);

  React.useEffect(() => {
    fetchExperiments();
  }, [fetchExperiments]);

  return {
    variants,
    loading,
    error,
    retry: fetchExperiments,
  };
};

export type ExperimentProps = {
  /** The experiment to consider */
  experiment: string;
  /** What to render while the experiment is loading */
  loader?: () => React.ReactNode;
  /** What to render if there is an error */
  error?: (input: { retry: () => void }) => React.ReactNode;
  /** Render prop for main content */
  children: (experiment: ExperimentType | null) => React.ReactNode;
};

export const Experiment = ({
  experiment,
  loader = () => null,
  error: errorRender = () => null,
  children,
}: ExperimentProps) => {
  const {
    variants: [exp],
    loading,
    error,
    retry,
  } = useExperiment([experiment]);

  if (loading) {
    return <>{loader()}</>;
  }

  if (error) {
    return <>{errorRender({ retry })}</>;
  }

  return <>{children(exp)}</>;
};
